import React from 'react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@digital-twins/design-system/components/ui/popover'
import { QualCreativeFilterLabels } from './contracts'

const FILTER_VISUAL_SIZE = 28
const PILL_TRANSITION = 'background-color 200ms ease, max-width 200ms ease, opacity 200ms ease, padding 200ms ease, margin 200ms ease'

export interface QualCreativeFilterOption {
  /** Stable editorial scope identifier (normally `asset:${assetId}`). */
  scopeId: string
  /** Stable bound creative identifier used for test hooks and selection. */
  assetId: string
  label: string
}

export interface QualCreativeFilterProps {
  options: QualCreativeFilterOption[]
  selectedId: string
  onSelect: (scopeId: string) => void
  labels: QualCreativeFilterLabels
}

/**
 * Local creative/scope selector for the qualitative editorial slide.
 *
 * This deliberately does not use ChartSegmentControl: creative scopes are
 * presentation evidence, not demographic axes or respondent segments.
 *
 * Rest state is a 28px icon circle. Hover, focus, and open expand the control
 * horizontally into a pill that reveals `labels.filterByAsset`.
 */
export function QualCreativeFilter({
  options,
  selectedId,
  onSelect,
  labels,
}: QualCreativeFilterProps) {
  const {
    triggerAriaLabel,
    title,
    explanation,
    allCreatives: allCreativesLabel,
    filterByAsset,
  } = labels
  const [open, setOpen] = React.useState(false)
  const [hover, setHover] = React.useState(false)
  const [focused, setFocused] = React.useState(false)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const restoreFocusOnClose = React.useRef(false)
  const optionRefs = React.useRef<Array<HTMLButtonElement | null>>([])
  const allOption = { scopeId: 'all', label: allCreativesLabel }
  const renderedOptions = [allOption, ...options]
  const selectedOption = renderedOptions.find((option) => option.scopeId === selectedId) ?? allOption
  const popoverId = React.useId()
  const selectedIndex = Math.max(
    0,
    renderedOptions.findIndex((option) => option.scopeId === selectedId),
  )
  const isExpanded = hover || focused || open
  const hasSpecificCreative = selectedId !== 'all'
  const triggerAccessibleName = `${triggerAriaLabel}, ${selectedOption.label}`

  function focusOption(index: number) {
    const nextIndex = (index + renderedOptions.length) % renderedOptions.length
    optionRefs.current[nextIndex]?.focus()
  }

  function handleOptionKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const currentIndex = optionRefs.current.findIndex((option) => option === document.activeElement)
    if (currentIndex < 0) {
      return
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault()
      focusOption(currentIndex + 1)
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault()
      focusOption(currentIndex - 1)
    } else if (event.key === 'Home') {
      event.preventDefault()
      focusOption(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      focusOption(renderedOptions.length - 1)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      restoreFocusOnClose.current = true
      setOpen(false)
      triggerRef.current?.focus()
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      optionRefs.current[currentIndex]?.click()
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
      }}
    >
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          data-testid="qual-creative-filter-trigger"
          aria-label={triggerAccessibleName}
          aria-expanded={open}
          aria-controls={popoverId}
          aria-haspopup="dialog"
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault()
              setOpen(true)
            }
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            height: FILTER_VISUAL_SIZE,
            minWidth: FILTER_VISUAL_SIZE,
            margin: 0,
            border: 0,
            padding: 0,
            borderRadius: 999,
            background: isExpanded
              ? 'var(--slide-chart-segment-control-hover-bg)'
              : 'transparent',
            color: 'var(--slide-text-primary)',
            font: 'inherit',
            cursor: 'pointer',
            transition: PILL_TRANSITION,
            overflow: 'hidden',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              position: 'relative',
              width: FILTER_VISUAL_SIZE,
              height: FILTER_VISUAL_SIZE,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M4 6h10M18 6h2M4 12h2M10 12h10M4 18h8M16 18h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <circle cx="16" cy="6" r="2" stroke="currentColor" strokeWidth="2" />
              <circle cx="8" cy="12" r="2" stroke="currentColor" strokeWidth="2" />
              <circle cx="14" cy="18" r="2" stroke="currentColor" strokeWidth="2" />
            </svg>
            {hasSpecificCreative ? (
              <span
                data-testid="qual-creative-filter-active-dot"
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: 7,
                  height: 7,
                  borderRadius: 999,
                  background: 'var(--slide-highlight, var(--slide-chart-segment-primary, #0ebfa5))',
                }}
              />
            ) : null}
          </span>
          <span
            data-testid="qual-creative-filter-label"
            aria-hidden="true"
            style={{
              maxWidth: isExpanded ? 220 : 0,
              opacity: isExpanded ? 1 : 0,
              marginRight: isExpanded ? 10 : 0,
              paddingLeft: isExpanded ? 2 : 0,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              fontSize: 12,
              fontWeight: 500,
              lineHeight: 1,
              transition: PILL_TRANSITION,
              pointerEvents: 'none',
            }}
          >
            {filterByAsset}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        id={popoverId}
        data-testid="qual-creative-filter-popover"
        align="start"
        side="top"
        role="dialog"
        aria-label={title}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          optionRefs.current[selectedIndex]?.focus()
        }}
        onCloseAutoFocus={(event) => {
          if (!restoreFocusOnClose.current) {
            event.preventDefault()
          }
          restoreFocusOnClose.current = false
        }}
        onKeyDown={handleOptionKeyDown}
        style={{
          width: 260,
          maxHeight: 'min(420px, calc(100vh - 96px))',
          padding: 8,
          overflow: 'hidden',
          // The popover portals out of the slide, so it would otherwise inherit the
          // app shell's theme (dark chrome) while its contents are painted with the
          // light slide tokens. Paint the surface from the same slide tokens the
          // chart-segment menu uses so the control reads as part of the slide.
          background: 'var(--slide-chart-segment-control-bg, var(--slide-bg, #ffffff))',
          border: '1px solid var(--slide-chart-segment-control-border, #d7d7d7)',
          borderRadius: 10,
          boxShadow: 'var(--slide-chart-segment-menu-shadow, 0 8px 24px rgb(0 0 0 / 12%))',
          color: 'var(--slide-chart-segment-control-fg, var(--slide-text-secondary, #384242))',
        }}
      >
        <div style={{ padding: '4px 8px 2px', fontSize: 13, fontWeight: 700, color: 'var(--slide-text-primary, #111111)' }}>{title}</div>
        <p style={{ margin: '4px 8px 10px', fontSize: 12, lineHeight: 1.45, color: 'var(--slide-text-muted, #737373)' }}>
          {explanation}
        </p>
        <div role="radiogroup" aria-label={title} style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 'min(280px, calc(100vh - 220px))', overflowY: 'auto' }}>
          {renderedOptions.map((option, index) => {
            const selected = option.scopeId === selectedId
            const assetId = 'assetId' in option ? option.assetId : undefined
            return (
              <button
                key={option.scopeId}
                ref={(node) => { optionRefs.current[index] = node }}
                type="button"
                role="radio"
                aria-checked={selected}
                tabIndex={index === selectedIndex ? 0 : -1}
                data-testid={assetId ? `qual-creative-option-${assetId}` : 'qual-creative-option-all'}
                onClick={(event) => {
                  onSelect(option.scopeId)
                  setOpen(false)
                  if (event.detail === 0) {
                    restoreFocusOnClose.current = true
                    triggerRef.current?.focus()
                  } else {
                    triggerRef.current?.blur()
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  minHeight: 36,
                  gap: 8,
                  padding: '7px 9px',
                  border: 0,
                  borderRadius: 7,
                  background: selected
                    ? 'var(--slide-chart-segment-control-selected, var(--slide-neutral-light, #edf2f7))'
                    : 'transparent',
                  color: 'var(--slide-chart-segment-control-fg, var(--slide-text-secondary, #384242))',
                  font: 'inherit',
                  fontSize: 13,
                  fontWeight: selected ? 600 : 400,
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                <span aria-hidden="true" style={{ width: 14, color: 'var(--slide-highlight, var(--slide-chart-segment-primary, #0ebfa5))', opacity: selected ? 1 : 0 }}>✓</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{option.label}</span>
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
